import { useState } from 'react'
import { Link2, PencilLine } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TokenUsageCopy, TokenUsagePerson, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import PersonAliasDialog from './PersonAliasDialog'
import UsageChannelIcon from './UsageChannelIcon'

interface Props {
  workerId: string
  items: TokenUsagePerson[]
  canonicalPeople: TokenUsageReport['canonical_people']
  copy: TokenUsageCopy
  canManage: boolean
}

const TH_CLS = 'h-9 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-faint-foreground'

function IdentitySummary({ person, copy }: { person: TokenUsagePerson; copy: TokenUsageCopy }) {
  if (!person.person_id || person.identifiers.length < 2) return null

  const countLabel = copy.people.identity_count_other.replace('%{count}', String(person.identifiers.length))

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={countLabel}
          className="inline-flex h-5 shrink-0 items-center gap-1 rounded-md border border-border bg-secondary/60 px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground transition-colors hover:border-agent/25 hover:text-agent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <Link2 className="size-3" strokeWidth={1.75} />
          {person.identifiers.length}
        </button>
      </TooltipTrigger>
      {/* Superfície neutra já é o padrão do TooltipContent; aqui só a caixa
          maior, com padding e alinhamento próprios da lista de identidades. */}
      <TooltipContent side="top" sideOffset={7} className="w-60 px-2.5 py-2 text-left">
        <p className="px-1 pb-1 text-[10.5px] font-medium text-muted-foreground">{countLabel}</p>
        <div className="space-y-1">
          {person.identifiers.map((identifier) => (
            <div
              key={`${identifier.channel}:${identifier.person_ref_hash}`}
              className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2 rounded-md bg-secondary/55 px-2 py-1.5"
            >
              <span className="flex size-5 items-center justify-center rounded border border-border bg-card">
                <UsageChannelIcon channel={identifier.channel} />
              </span>
              <div className="flex min-w-0 items-center justify-between gap-3 text-[10.5px]">
                <span className="shrink-0 text-muted-foreground">{identifier.channel_label}</span>
                <span className="truncate font-medium text-foreground">{identifier.source_name || person.name}</span>
              </div>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

export default function PeopleBreakdown({ workerId, items, canonicalPeople, copy, canManage }: Props) {
  const [selectedPerson, setSelectedPerson] = useState<TokenUsagePerson | null>(null)

  if (items.length === 0) {
    return <p className="py-8 text-center text-[13px] text-muted-foreground">{copy.no_dimension_data}</p>
  }

  return (
    <>
      <Table className="table-fixed text-[13px]">
        <TableHeader>
          <TableRow>
            <TableHead className={TH_CLS}>{copy.origin}</TableHead>
            <TableHead className={`w-28 text-right ${TH_CLS}`}>{copy.participation}</TableHead>
            <TableHead className={`w-10 ${TH_CLS}`}><span className="sr-only">{copy.actions}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((person) => {
            const actionLabel = person.person_id ? copy.people.manage : copy.people.organize

            return (
              <TableRow key={person.key}>
                <TableCell className="h-14 min-w-0 whitespace-normal">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium text-foreground">{person.name}</span>
                      <IdentitySummary person={person} copy={copy} />
                    </div>
                    <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                      {person.channels.map((channel) => (
                        <Badge key={channel} variant="secondary" className="gap-1 text-[10px] font-medium">
                          <UsageChannelIcon channel={channel} />
                          {channel}
                        </Badge>
                      ))}
                      {person.source_name && person.source_name !== person.name && (
                        <span className="truncate text-[10.5px] text-faint-foreground">
                          {copy.people.source_name}: {person.source_name}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right text-[12px] font-medium tabular-nums text-muted-foreground">
                  {person.share_label ?? '0'}%
                </TableCell>
                <TableCell className="text-right">
                  {canManage && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={actionLabel}
                          onClick={() => setSelectedPerson(person)}
                          className="size-7 active:scale-[0.96]"
                        >
                          <PencilLine className="size-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{actionLabel}</TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <PersonAliasDialog
        workerId={workerId}
        person={selectedPerson}
        discoveredPeople={items}
        canonicalPeople={canonicalPeople}
        copy={copy}
        onClose={() => setSelectedPerson(null)}
      />
    </>
  )
}
